import type { QueryClient } from '@tanstack/react-query'
import type { Locale } from '~/lib/i18n/shared'
import {
  createRootRouteWithContext,
  HeadContent,
  Outlet,
  Scripts,
} from '@tanstack/react-router'
import * as React from 'react'
import { Toaster } from 'sonner'
// TabeTabi 错误边界预留，如果您有自定义的组件请替换，这里用原生 fallback
import { DefaultCatchBoundary } from '~/components/errors/default-catch-boundary'
import { NotFound404 } from '~/components/errors/not-found-404'
import { DefaultPending } from '~/components/misc/default-pending'
import { IntlProvider } from '~/components/providers/intl-provider'
import { LocalizedAuthProvider } from '~/components/providers/localized-auth-provider'
import { ThemeProvider } from '~/components/providers/theme-provider'

import { getCurrentLocale } from '~/lib/i18n/client'
import { defaultLocale } from '~/lib/i18n/shared'
import appCss from '~/styles/app.css?url'

interface RouterContext {
  queryClient: QueryClient
}

export const Route = createRootRouteWithContext<RouterContext>()({
  head: () => ({
    meta: [
      { charSet: 'utf-8' },
      { name: 'viewport', content: 'width=device-width, initial-scale=1' },
    ],
    links: [
      { rel: 'stylesheet', href: appCss },
      { rel: 'icon', type: 'image/x-icon', href: '/favicon.ico' },
      { rel: 'icon', type: 'image/png', sizes: '32x32', href: '/favicon-32x32.png' },
      { rel: 'icon', type: 'image/png', sizes: '16x16', href: '/favicon-16x16.png' },
      { rel: 'apple-touch-icon', sizes: '180x180', href: '/apple-touch-icon.png' },
    ],
    scripts: [
      {
        type: 'application/ld+json',
        children: JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'Organization',
          name: 'TabeTabi',
          url: 'https://tabetabi.cc',
          logo: 'https://tabetabi.cc/logo.png',
        }),
      },
    ],
  }),
  errorComponent: props => (
    <RootDocumentForError>
      <DefaultCatchBoundary {...props} />
    </RootDocumentForError>
  ),
  notFoundComponent: () => (
    <RootDocumentForError>
      <NotFound404 />
    </RootDocumentForError>
  ),
  pendingComponent: () => (
    <RootDocumentForError>
      <DefaultPending />
    </RootDocumentForError>
  ),
  component: RootComponent,
})

import { AuthGuardModal } from '~/components/auth/AuthGuardModal'
import { InsufficientCreditsModal } from '~/features/billing/components/InsufficientCreditsModal'

function RootComponent() {
  return (
    <RootDocument>
      <ThemeProvider>
        <IntlProvider>
          <LocalizedAuthProvider>
            <Outlet />
            <Toaster richColors position="top-right" />
            <AuthGuardModal />
            <InsufficientCreditsModal />
          </LocalizedAuthProvider>
        </IntlProvider>
      </ThemeProvider>
    </RootDocument>
  )
}

/**
 * 专用于错误和 404 页面的根文档。
 * 在 SSR 期间发生错误时，如果试图执行动态 Locale 探测（getCurrentLocale），
 * 很容易发生 Context 缺失或引发 Hydration 失败。
 * 所以错误页严格使用 defaultLocale 兜底。
 */
function RootDocumentForError({ children }: { children: React.ReactNode }) {
  return (
    <html lang={defaultLocale} suppressHydrationWarning>
      <head>
        <HeadContent />
      </head>
      <body>
        <React.Suspense fallback={<div>Loading...</div>}>
          {children}
        </React.Suspense>
        <Scripts />
      </body>
    </html>
  )
}

/**
 * 正常页面的根文档，负责执行正常的国际化探嗅。
 */
function RootDocument({ children }: { children: React.ReactNode }) {
  let locale: Locale = defaultLocale
  try {
    locale = getCurrentLocale()
  }
  catch (e) {
    // Ignore
  }

  return (
    <html lang={locale} suppressHydrationWarning>
      <head>
        <HeadContent />
      </head>
      <body>
        <React.Suspense fallback={<div>Loading...</div>}>
          {children}
        </React.Suspense>
        <Scripts />
      </body>
    </html>
  )
}
